/**
 * Page-level and row-level action permissions from interface elements.
 */

import { useInterfaceElements } from './useInterfaceElements';

/**
 * Row-level action check: `_permissions[actionId]` when present, otherwise `globalFallback`.
 */
export function getRowPermission(
  row: { _permissions?: Record<string, boolean> } | null | undefined,
  actionId: string,
  globalFallback: boolean,
): boolean {
  if (!row?._permissions) return globalFallback;
  return row._permissions[actionId] ?? globalFallback;
}

/**
 * UI action permissions for an interface (`elementType: 'action'` in
 * `interface_elements`, granted via `role_interface_elements`).
 */
export function usePageActions(interfaceName: string) {
  const { elements } = useInterfaceElements(interfaceName);

  const find = (elementId: string) => elements.find((el) => el.elementId === elementId);

  /** Types from `action-create-{type}` (replaces the legacy global `action-create`). */
  const creatableTypes = elements
    .filter((el) => el.elementType === 'action' && el.elementId.startsWith('action-create-'))
    .map((el) => el.elementId.replace('action-create-', ''));

  return {
    /** True if the role has any `action-create-{type}`, `action-create`, or `create-role`. */
    canCreate:
      creatableTypes.length > 0 ||
      !!find('action-create') ||
      elements.some(
        (el) =>
          el.elementType === 'action' &&
          (el.config as { subtype?: string })?.subtype === 'create-role',
      ),

    /** Types from `action-create-{type}` — used to filter the type-picker modal. */
    creatableTypes,

    /** `action-edit-row`, or at least one `option-edit-*` (`subtype: 'edit-role'`). */
    canEditRow:
      !!find('action-edit-row') ||
      elements.some(
        (el) =>
          el.elementType === 'action' &&
          (el.config as { subtype?: string })?.subtype === 'edit-role',
      ),

    /** `action-delete-row`, or at least one `option-delete-*` (`subtype: 'delete-role'`). */
    canDeleteRow:
      !!find('action-delete-row') ||
      elements.some(
        (el) =>
          el.elementType === 'action' &&
          (el.config as { subtype?: string })?.subtype === 'delete-role',
      ),
    canViewRow: !!find('action-view-row'),
    canManageAttendance: !!find('action-manage-attendance'),
    canImportCsv: !!find('action-import-csv'),

    /** Accepts `action-export-excel` and the legacy `action-export-csv` / `action-export`. */
    canExportCsv: !!(
      find('action-export-excel') ||
      find('action-export-csv') ||
      find('action-export')
    ),

    /** Alias of canExportCsv for pages that still read `canExportExcel`. */
    canExportExcel: !!(
      find('action-export-excel') ||
      find('action-export-csv') ||
      find('action-export')
    ),

    canValidate: !!find('action-validate'),
    canCancelRow: !!find('action-cancel-row'),
    canViewReport: !!find('action-view-report'),
    canValidateCriminalRecord: !!find('action-validate-criminal-record'),
    canJoinWelcomeMeeting: !!find('action-join-meeting-welcome-meeting'),
    canJoinInformationMeeting: !!find('action-join-meeting-information-meeting'),

    // reports-remote: per-type edit / validate
    canEditRowTutoring: !!find('action-edit-row-tutoring'),
    canEditRowInformationMeeting: !!find('action-edit-row-information-meeting'),
    /** In-person alias — same element_id as canEditRowInformationMeeting. */
    canEditRowInformation: !!find('action-edit-row-information-meeting'),
    canEditRowWelcome: !!find('action-edit-row-welcome-meeting'),
    canEditRowFieldEvent: !!find('action-edit-row-field-event'),

    canValidateTutoring: !!find('action-validate-tutoring'),
    canValidateInformationMeeting: !!find('action-validate-information-meeting'),
    /** In-person alias — same element_id as canValidateInformationMeeting. */
    canValidateInformation: !!find('action-validate-information-meeting'),
    canValidateWelcome: !!find('action-validate-welcome-meeting'),
    canValidateFieldEvent: !!find('action-validate-field-event'),

    // sessions-remote: per-type report navigation
    canViewReportTutoring: !!find('action-view-report-tutoring'),
    canViewReportInformationMeeting: !!find('action-view-report-information-meeting'),
    /** In-person alias — same element_id as canViewReportInformationMeeting. */
    canViewReportInformation: !!find('action-view-report-information-meeting'),
    canViewReportWelcome: !!find('action-view-report-welcome-meeting'),
    canViewReportFieldEvent: !!find('action-view-report-field-event'),

    canDissociate: !!find('action-dissociate'),
    canPrevalidate: !!find('action-prevalidate'),
    canValidateAssignment: !!find('action-validate'),
    canArchiveRow: !!find('action-archive-row'),
    canReactivateRow: !!find('action-reactivate-row'),
  };
}
